import { TrophyIcon, SparklesIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import List from '@client/uikit/List';
import ChangeValue from '../common/ChangeValue';
import { useCommonNavigate } from '@client/component/CommonLink';

interface RankingStrategy {
    hash: string;
    rank: number;
    name: string;
    userName?: string;
    returnRate: number;
    topHoldings: Array<{
        ticker: string;
        weight: number;
    }>;
}

interface ChallengeRankingListProps {
    strategies: RankingStrategy[];
    isLoading?: boolean;
}

const ChallengeRankingList = observer(({ strategies, isLoading }: ChallengeRankingListProps) => {
    const { navigate } = useCommonNavigate();

    if (isLoading) {
        return (
            <div className="flex flex-col gap-3">
                <div className="skeleton h-48 w-full rounded-xl" />
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="skeleton h-20 w-full rounded-lg" />
                ))}
            </div>
        );
    }

    if (strategies.length === 0) {
        return (
            <div className="text-base-content/60 flex h-40 items-center justify-center text-center text-sm">
                아직 참여한 전략이 없습니다
            </div>
        );
    }

    const firstPlace = strategies[0];
    const restStrategies = strategies.slice(1);

    // 주요 종목 요약 문구 생성 (예: "3개 종목")
    const getHoldingsSummary = (holdings: RankingStrategy['topHoldings']) => {
        const count = holdings.length;
        return `${count}개 종목`;
    };

    return (
        <div className="flex flex-col gap-3">
            {/* 1위 전략 - 심플하게 강조 */}
            <div
                className="border-base-300 bg-base-100 hover:border-primary/50 cursor-pointer rounded-lg border p-4 transition-all"
                onClick={() => navigate(`/strategy/${firstPlace.hash}`)}
            >
                {/* 1위 배지와 작성자 */}
                <div className="mb-3 flex items-center gap-2">
                    <div className="badge badge-warning badge-sm gap-1 font-semibold">
                        <TrophyIcon className="size-3" />
                        1위
                    </div>
                    {firstPlace.userName && (
                        <span className="text-base-content/60 text-xs">{firstPlace.userName}</span>
                    )}
                </div>

                {/* 전략명 */}
                <h3 className="text-base-content mb-2 text-lg font-bold">{firstPlace.name}</h3>

                {/* 핵심 문구: "X개 종목으로 +Y%" */}
                <div className="mb-3 flex items-baseline gap-2">
                    <span className="text-base-content/60 text-sm">
                        {getHoldingsSummary(firstPlace.topHoldings)}으로
                    </span>
                    <ChangeValue
                        value={firstPlace.returnRate * 100}
                        suffix="%"
                        sign={true}
                        round={2}
                        className="text-2xl font-bold tabular-nums"
                    />
                </div>

                {/* 종목 구성 */}
                <div className="mb-3 flex flex-col gap-2">
                    {firstPlace.topHoldings.slice(0, 3).map((holding) => (
                        <div key={holding.ticker} className="flex items-center gap-2">
                            <div className="flex w-20 items-baseline gap-1">
                                <span className="text-base-content text-xs font-semibold">
                                    {holding.ticker}
                                </span>
                                <span className="text-base-content/60 text-xs tabular-nums">
                                    {(holding.weight * 100).toFixed(0)}%
                                </span>
                            </div>
                            {/* 비중 바 */}
                            <div className="bg-base-200 h-1.5 flex-1 overflow-hidden rounded-full">
                                <div
                                    className="bg-base-content/30 h-full rounded-full"
                                    style={{ width: `${holding.weight * 100}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA 버튼 */}
                <button
                    className="btn btn-ghost btn-sm btn-block text-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/strategy/${firstPlace.hash}`);
                    }}
                >
                    자세히 보기
                </button>
            </div>

            {/* 힌트 메시지 - 간결하게 */}
            {restStrategies.length > 0 && (
                <div className="text-base-content/60 px-1 text-xs">
                    <SparklesIcon className="mr-1 mb-0.5 inline size-3" />
                    1위는 {getHoldingsSummary(firstPlace.topHoldings)}으로 이런 성과를 냈어요.
                </div>
            )}

            {/* 2위 이하 리스트 */}
            {restStrategies.length > 0 && (
                <List>
                    {restStrategies.map((strategy) => (
                        <List.Item key={strategy.hash} link={`/strategy/${strategy.hash}`}>
                            {/* 순위 - 공간 최소화 */}
                            <div className="mr-3 w-6 shrink-0 text-center">
                                {strategy.rank === 2 ? (
                                    <div className="badge badge-neutral badge-sm font-semibold">
                                        {strategy.rank}
                                    </div>
                                ) : strategy.rank === 3 ? (
                                    <div className="badge badge-sm border-0 bg-amber-700 font-semibold text-white">
                                        {strategy.rank}
                                    </div>
                                ) : (
                                    <span className="text-base-content/40 text-sm font-semibold tabular-nums">
                                        {strategy.rank}
                                    </span>
                                )}
                            </div>

                            {/* 전략 정보 */}
                            <div className="list-col-grow min-w-0">
                                <List.ItemTitle className="text-sm font-semibold">
                                    {strategy.name}
                                </List.ItemTitle>
                                {strategy.userName && (
                                    <div className="text-base-content/50 text-xs">
                                        {strategy.userName}
                                    </div>
                                )}
                                {/* 종목 구성 */}
                                <div className="text-base-content/50 mt-0.5 text-xs">
                                    {strategy.topHoldings
                                        .slice(0, 3)
                                        .map((h) => `${h.ticker} ${(h.weight * 100).toFixed(0)}%`)
                                        .join(' • ')}
                                </div>
                            </div>

                            {/* 수익률 */}
                            <div className="ml-2 shrink-0 text-right">
                                <ChangeValue
                                    value={strategy.returnRate * 100}
                                    suffix="%"
                                    sign={true}
                                    round={2}
                                    className="text-lg font-bold tabular-nums"
                                />
                            </div>
                        </List.Item>
                    ))}
                </List>
            )}
        </div>
    );
});

export default ChallengeRankingList;
